import React, { useState, useEffect } from 'react';
import { Modal, Button, Badge, Row, Col, Table, Form, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faCashRegister,
  faUtensils,
  faBed,
  faBoxes,
  faFileInvoiceDollar,
  faSync,
  faChevronDown,
  faChevronUp,
  faLock,
  faShieldAlt,
  faMobileAlt,
  faBan
} from '@fortawesome/free-solid-svg-icons';
import './NightAuditCheckpointsModal.scss';

const NightAuditCheckpointsModal = ({
  show,
  onHide,
  checkpointsData,
  loading,
  onRefresh,
  onConfirmClose,
  closing,
  onGoToTableView,
  onGoToOrderList,
  onGoToDigitalOrders
}) => {
  const [expandedTiers, setExpandedTiers] = useState({
    tier1: true,
    tier2: true,
    tier3: true,
    tier4: false,
    tier5: false
  });

  const [registerInputs, setRegisterInputs] = useState({});
  const [managerOverride, setManagerOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [validationError, setValidationError] = useState('');

  const evaluation = checkpointsData?.audit_evaluation || {};
  const tiers = checkpointsData?.tiers || {};
  const summary = checkpointsData?.executive_summary || {};
  const store = checkpointsData?.store || {};
  const businessDay = checkpointsData?.business_day || {};

  // Auto-expand tiers that need attention; collapse clean ones
  useEffect(() => {
    const t = checkpointsData?.tiers || {};
    const mapping = {
      tier1: t.tier1_orders?.status,
      tier2: t.tier2_registers?.status,
      tier3: t.tier3_folio?.status,
      tier4: t.tier4_financial?.status,
      tier5: t.tier5_inventory?.status
    };
    setExpandedTiers(prev => {
      const next = { ...prev };
      Object.entries(mapping).forEach(([key, status]) => {
        if (status === 'BLOCKED' || status === 'WARNING') next[key] = true;
        if (status === 'DISABLED') next[key] = false;
      });
      return next;
    });
  }, [checkpointsData?.tiers]);

  // Initialize register inputs for open registers
  useEffect(() => {
    if (tiers?.tier2_registers?.details?.registers) {
      const initialInputs = {};
      tiers.tier2_registers.details.registers.forEach((reg) => {
        initialInputs[reg.register_id] = {
          actual_closing_cash: reg.is_open ? reg.expected_closing_cash : reg.actual_closing_cash,
          credit_card_slips: reg.credit_card_slips || 0,
          cheques: reg.cheques || 0,
          close_note: reg.closing_note || ''
        };
      });
      setRegisterInputs(initialInputs);
      setManagerOverride(false);
      setOverrideReason('');
      setValidationError('');
    }
  }, [tiers?.tier2_registers?.details?.registers]);

  const toggleTier = (tierKey) => {
    setExpandedTiers((prev) => ({
      ...prev,
      [tierKey]: !prev[tierKey]
    }));
  };

  const handleRegisterInputChange = (regId, field, value) => {
    setRegisterInputs((prev) => ({
      ...prev,
      [regId]: {
        ...(prev[regId] || {}),
        [field]: field === 'close_note' ? value : (value === '' ? '' : Number(value))
      }
    }));
  };

  const getStatusBadge = (status) => {
    if (status === 'PASSED') {
      return <Badge bg="success"><FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Passed</Badge>;
    }
    if (status === 'WARNING') {
      return <Badge bg="warning" text="dark"><FontAwesomeIcon icon={faExclamationTriangle} className="me-1" /> Warning</Badge>;
    }
    if (status === 'DISABLED') {
      return <Badge bg="secondary"><FontAwesomeIcon icon={faBan} className="me-1" /> Disabled</Badge>;
    }
    return <Badge bg="danger"><FontAwesomeIcon icon={faTimesCircle} className="me-1" /> Blocked</Badge>;
  };

  const handleSubmitClose = () => {
    setValidationError('');

    // Check if hard blocked
    if (!evaluation.can_close) {
      setValidationError('Cannot close day: Please resolve all Hard Blocked checkpoints first.');
      return;
    }

    // Check if soft warning override required
    if (evaluation.requires_manager_override && !managerOverride) {
      setValidationError('Manager Override is required to acknowledge active warnings before closing.');
      return;
    }

    if (evaluation.requires_manager_override && managerOverride && !overrideReason.trim()) {
      setValidationError('Please provide a reason for the Manager Override.');
      return;
    }

    // Format register close inputs
    const registerPayload = Object.keys(registerInputs).map((id) => ({
      register_id: Number(id),
      closing_amount: Number(registerInputs[id]?.actual_closing_cash || 0),
      credit_card_slips: Number(registerInputs[id]?.credit_card_slips || 0),
      cheques: Number(registerInputs[id]?.cheques || 0),
      close_note: registerInputs[id]?.close_note || overrideReason
    }));

    onConfirmClose({
      manager_override: managerOverride,
      override_reason: overrideReason,
      registers: registerPayload
    });
  };

  return (
    <Modal
      show={show}
      onHide={closing ? null : onHide}
      className="night-audit-modal"
      backdrop="static"
      centered
      size="lg"
    >
      <Modal.Header closeButton={!closing} className="night-audit-header">
        <div>
          <Modal.Title>
            <FontAwesomeIcon icon={faShieldAlt} className="text-primary" />
            Advance Night Audit Checkpoints
          </Modal.Title>
          <div className="subtitle-info">
            Store: <strong>{store.name || 'Current Store'}</strong>
            {store.business_type && <> | <Badge bg="secondary">{store.business_type}</Badge></>}
            {' | Business Date: '}<strong>{businessDay.business_date || '--'}</strong>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="p-4">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="mt-3 text-muted">Running pre-close audit verification...</div>
          </div>
        ) : (
          <>
            {/* Executive Summary Stats Banner */}
            <div className="executive-banner">
              <Row>
                <Col xs={6} md={3} className="stat-item border-end mb-2 mb-md-0">
                  <div className="stat-label">Gross Sales</div>
                  <div className="stat-value text-success">
                    {summary.gross_sales !== undefined ? summary.gross_sales.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '0.00'}
                  </div>
                </Col>
                <Col xs={6} md={3} className="stat-item border-end mb-2 mb-md-0">
                  <div className="stat-label">Tax Collected</div>
                  <div className="stat-value text-primary">
                    {summary.total_tax !== undefined ? summary.total_tax.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '0.00'}
                  </div>
                </Col>
                <Col xs={6} md={3} className="stat-item border-end">
                  <div className="stat-label">Completed Orders</div>
                  <div className="stat-value text-dark">
                    {summary.completed_orders || 0}
                  </div>
                </Col>
                <Col xs={6} md={3} className="stat-item">
                  <div className="stat-label">Cash Variance</div>
                  <div className={`stat-value ${summary.total_cash_variance !== 0 ? 'text-danger' : 'text-success'}`}>
                    {summary.total_cash_variance !== undefined ? summary.total_cash_variance.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '0.00'}
                  </div>
                </Col>
              </Row>
            </div>

            {/* Readiness Verdict Strip */}
            {Object.keys(tiers).length > 0 && (() => {
              const tierList = [tiers.tier1_orders, tiers.tier2_registers, tiers.tier3_folio, tiers.tier4_financial, tiers.tier5_inventory].filter(Boolean);
              // DISABLED tiers (non-selling store skips) don't count toward readiness
              const activeTiers = tierList.filter(t => t.status !== 'DISABLED');
              const blockedCount = activeTiers.filter(t => t.status === 'BLOCKED').length;
              const warningCount = activeTiers.filter(t => t.status === 'WARNING').length;
              const passedCount = activeTiers.filter(t => t.status === 'PASSED').length;
              const readinessPct = activeTiers.length ? Math.round((passedCount / activeTiers.length) * 100) : 0;
              return (
                <div className={`readiness-strip mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2 ${blockedCount > 0 ? 'readiness-blocked' : warningCount > 0 ? 'readiness-warning' : 'readiness-ready'}`}>
                  <div className="d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={blockedCount > 0 ? faTimesCircle : warningCount > 0 ? faExclamationTriangle : faCheckCircle} size="lg" />
                    <strong>
                      {blockedCount > 0
                        ? `${blockedCount} blocker(s) - resolve before closing`
                        : warningCount > 0
                          ? `Ready - ${warningCount} warning(s) need manager sign-off`
                          : 'Ready to close - all checkpoints passed'}
                    </strong>
                  </div>
                  <div className="d-flex align-items-center gap-2 small">
                    <span className="text-nowrap"><strong>{passedCount}/{tierList.length}</strong> passed</span>
                    <div className="readiness-bar" style={{ width: '120px' }}>
                      <div className="readiness-bar-fill" style={{ width: `${readinessPct}%` }} />
                    </div>
                    {warningCount > 0 && <Badge bg="warning" text="dark">{warningCount} warning(s)</Badge>}
                    {blockedCount > 0 && <Badge bg="danger">{blockedCount} blocked</Badge>}
                  </div>
                </div>
              );
            })()}

            {/* Checkpoint Tier 1: Orders, Tables & Kitchen KOTs */}
            <div className={`checkpoint-card status-${(tiers.tier1_orders?.status || 'passed').toLowerCase()}`}>
              <div className="card-header" onClick={() => toggleTier('tier1')}>
                <div className="header-title">
                  <FontAwesomeIcon icon={faUtensils} />
                  <span>Tier 1: Orders, Tables & Kitchen Tickets</span>
                </div>
                <div className="header-actions">
                  {getStatusBadge(tiers.tier1_orders?.status)}
                  <FontAwesomeIcon icon={expandedTiers.tier1 ? faChevronUp : faChevronDown} className="text-muted" />
                </div>
              </div>
              {expandedTiers.tier1 && (
                <div className="card-body">
                  <p className="message-text">{tiers.tier1_orders?.message}</p>
                  {tiers.tier1_orders?.details?.digital_orders_count > 0 && (
                    <div className="p-2 px-3 mb-3 bg-info bg-opacity-10 border border-info rounded d-flex justify-content-between align-items-center">
                      <div className="text-info">
                        <FontAwesomeIcon icon={faMobileAlt} className="me-2" />
                        <strong>{tiers.tier1_orders.details.digital_orders_count} Pending Digital Menu Order(s):</strong> Placed via QR/Digital Menu and awaiting acceptance or cancellation.
                      </div>
                      {onGoToDigitalOrders && (
                        <Button variant="outline-info" size="sm" onClick={onGoToDigitalOrders}>
                          View Digital Orders
                        </Button>
                      )}
                    </div>
                  )}

                  {tiers.tier1_orders?.details?.open_orders_count > 0 && (
                    <>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong className="text-danger">Unclosed Orders ({tiers.tier1_orders.details.open_orders_count}):</strong>
                        <div>
                          {tiers.tier1_orders.details.digital_orders_count > 0 && onGoToDigitalOrders && (
                            <Button variant="outline-info" size="sm" className="me-2" onClick={onGoToDigitalOrders}>
                              Digital Orders ({tiers.tier1_orders.details.digital_orders_count})
                            </Button>
                          )}
                          {onGoToTableView && (
                            <Button variant="outline-primary" size="sm" onClick={onGoToTableView}>
                              Go to Table View
                            </Button>
                          )}
                        </div>
                      </div>
                      <Table responsive bordered hover size="sm" className="detail-table">
                        <thead>
                          <tr>
                            <th>Order #</th>
                            <th>Amount</th>
                            <th>Table / Room / Source</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tiers.tier1_orders.details.open_orders.map((ord) => (
                            <tr key={ord.id}>
                              <td>
                                {ord.order_number}
                                {(ord.order_origin === 'DIGITAL_MENU' || ord.status === 6) && (
                                  <Badge bg="info" className="ms-1">Digital Menu</Badge>
                                )}
                              </td>
                              <td>{ord.total_order_amount_rounded}</td>
                              <td>
                                {ord.order_origin === 'DIGITAL_MENU' ? (
                                  ord.table_id ? `Table ${ord.table_id} (Digital)` : 'Digital Menu (Takeaway/Delivery)'
                                ) : (
                                  ord.room_number ? `Room ${ord.room_number}` : (ord.table_id ? `Table ${ord.table_id}` : 'Counter')
                                )}
                              </td>
                              <td>
                                <Badge bg={ord.status === 6 ? 'info' : ord.status === 5 ? 'warning' : ord.status === 3 ? 'danger' : 'secondary'}>
                                  {ord.status === 6 ? 'Digital (Awaiting Accept)' : ord.status === 3 ? 'Payment Attempted' : ord.status === 5 ? 'In Kitchen' : ord.status === 2 ? 'Hold' : 'Open'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Checkpoint Tier 2: Cash Registers & Drawer Reconciliation */}
            <div className={`checkpoint-card status-${(tiers.tier2_registers?.status || 'passed').toLowerCase()}`}>
              <div className="card-header" onClick={() => toggleTier('tier2')}>
                <div className="header-title">
                  <FontAwesomeIcon icon={faCashRegister} />
                  <span>Tier 2: Cash Registers & Drawer Reconciliation</span>
                </div>
                <div className="header-actions">
                  {getStatusBadge(tiers.tier2_registers?.status)}
                  <FontAwesomeIcon icon={expandedTiers.tier2 ? faChevronUp : faChevronDown} className="text-muted" />
                </div>
              </div>
              {expandedTiers.tier2 && (
                <div className="card-body">
                  <p className="message-text">{tiers.tier2_registers?.message}</p>
                  {tiers.tier2_registers?.details?.registers?.length > 0 && (
                    <Table responsive bordered size="sm" className="register-input-table">
                      <thead>
                        <tr>
                          <th>Counter / Cashier</th>
                          <th>Status</th>
                          <th>Opening Float</th>
                          <th>Cash Sales</th>
                          <th>Server Expected</th>
                          <th style={{ width: '130px' }}>Counted Cash</th>
                          <th>Variance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tiers.tier2_registers.details.registers.map((reg) => {
                          const inputActual = registerInputs[reg.register_id]?.actual_closing_cash !== undefined
                            ? registerInputs[reg.register_id].actual_closing_cash
                            : reg.actual_closing_cash;
                          const currentVariance = Number(inputActual) - Number(reg.expected_closing_cash);

                          return (
                            <tr key={reg.register_id}>
                              <td>
                                <strong>{reg.counter_name}</strong>
                                <div className="text-muted small">{reg.cashier_name}</div>
                              </td>
                              <td>
                                <Badge bg={reg.is_open ? 'danger' : 'success'}>
                                  {reg.is_open ? 'Open' : 'Closed'}
                                </Badge>
                                {reg.is_joined_session && (
                                  <Badge bg="info" className="ms-1" title="Joined session - drawer counted by owner">Joined</Badge>
                                )}
                                {reg.is_stale_session && (
                                  <Badge bg="warning" text="dark" className="ms-1" title={`Opened on earlier business day #${reg.business_day_id} - never closed`}>Stale</Badge>
                                )}
                              </td>
                              <td>{reg.opening_float?.toFixed(2)}</td>
                              <td>{reg.cash_sales?.toFixed(2)}</td>
                              <td><strong>{reg.expected_closing_cash?.toFixed(2)}</strong></td>
                              <td>
                                {reg.is_joined_session ? (
                                  <span className="text-muted small">—</span>
                                ) : reg.is_open ? (
                                  <Form.Control
                                    type="number"
                                    size="sm"
                                    min="0"
                                    step="0.01"
                                    value={inputActual}
                                    onChange={(e) => handleRegisterInputChange(reg.register_id, 'actual_closing_cash', e.target.value)}
                                  />
                                ) : (
                                  <span>{reg.actual_closing_cash?.toFixed(2)}</span>
                                )}
                              </td>
                              <td className={currentVariance < 0 ? 'variance-negative' : (currentVariance > 0 ? 'variance-positive' : 'text-muted')}>
                                {reg.is_joined_session
                                  ? <span className="text-muted small">—</span>
                                  : (currentVariance > 0 ? `+${currentVariance.toFixed(2)}` : currentVariance.toFixed(2))}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  )}
                </div>
              )}
            </div>

            {/* Checkpoint Tier 3: Hotel Guest Folio / Room Bill Posting */}
            <div className={`checkpoint-card status-${(tiers.tier3_folio?.status || 'passed').toLowerCase()}`}>
              <div className="card-header" onClick={() => toggleTier('tier3')}>
                <div className="header-title">
                  <FontAwesomeIcon icon={faBed} />
                  <span>Tier 3: Folio, Credit &amp; Bill Posting</span>
                </div>
                <div className="header-actions">
                  {getStatusBadge(tiers.tier3_folio?.status)}
                  <FontAwesomeIcon icon={expandedTiers.tier3 ? faChevronUp : faChevronDown} className="text-muted" />
                </div>
              </div>
              {expandedTiers.tier3 && (
                <div className="card-body">
                  <p className="message-text">{tiers.tier3_folio?.message}</p>
                  {tiers.tier3_folio?.details?.unacknowledged_count > 0 && (
                    <Table responsive bordered hover size="sm" className="detail-table">
                      <thead>
                        <tr>
                          <th>Order #</th>
                          <th>Guest Name</th>
                          <th>Room #</th>
                          <th>Amount</th>
                          <th>PMS Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tiers.tier3_folio.details.unacknowledged_orders.map((b) => (
                          <tr key={b.id}>
                            <td>{b.order_number}</td>
                            <td>{b.customer_name || '-'}</td>
                            <td>Room {b.room_number || '-'}</td>
                            <td>{b.total_order_amount_rounded}</td>
                            <td><Badge bg="danger">Missing PMS Ack</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                  {tiers.tier3_folio?.details?.pending_settlement_count > 0 && (
                    <>
                      <strong className="text-warning d-block mb-2">Awaiting PMS Settlement ({tiers.tier3_folio.details.pending_settlement_count}):</strong>
                      <Table responsive bordered hover size="sm" className="detail-table">
                        <thead>
                          <tr>
                            <th>Order #</th>
                            <th>Guest Name</th>
                            <th>Room #</th>
                            <th>Amount</th>
                            <th>Settlement</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tiers.tier3_folio.details.pending_settlements.map((b) => (
                            <tr key={`ps-${b.id}`}>
                              <td>{b.order_number}</td>
                              <td>{b.customer_name || '-'}</td>
                              <td>Room {b.room_number || '-'}</td>
                              <td>{b.total_order_amount_rounded}</td>
                              <td><Badge bg="warning" text="dark">{b.pms_settlement_status || 'Unsettled'}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}
                  {tiers.tier3_folio?.details?.open_folio_charges_count > 0 && (
                    <>
                      <strong className="text-warning d-block mb-2">Unsettled Folio / Credit Charges ({tiers.tier3_folio.details.open_folio_charges_count}):</strong>
                      <Table responsive bordered hover size="sm" className="detail-table">
                        <thead>
                          <tr>
                            <th>Order #</th>
                            <th>Customer</th>
                            <th>Amount</th>
                            <th>Pending</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tiers.tier3_folio.details.open_folio_charges.map((b) => (
                            <tr key={`fc-${b.id}`}>
                              <td>{b.order_number}</td>
                              <td>{b.customer_name || '-'}</td>
                              <td>{b.total_order_amount_rounded}</td>
                              <td><Badge bg="warning" text="dark">{b.total_pending_amount ?? b.total_order_amount_rounded}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Checkpoint Tier 4: Financial & Tender Balancing */}
            <div className={`checkpoint-card status-${(tiers.tier4_financial?.status || 'passed').toLowerCase()}`}>
              <div className="card-header" onClick={() => toggleTier('tier4')}>
                <div className="header-title">
                  <FontAwesomeIcon icon={faFileInvoiceDollar} />
                  <span>Tier 4: Financial & Tender Balancing</span>
                </div>
                <div className="header-actions">
                  {getStatusBadge(tiers.tier4_financial?.status)}
                  <FontAwesomeIcon icon={expandedTiers.tier4 ? faChevronUp : faChevronDown} className="text-muted" />
                </div>
              </div>
              {expandedTiers.tier4 && (
                <div className="card-body">
                  <p className="message-text">{tiers.tier4_financial?.message}</p>
                  <Row>
                    <Col md={6}>
                      <h6>Tender Method Breakdown</h6>
                      <Table responsive bordered size="sm">
                        <tbody>
                          {tiers.tier4_financial?.details?.tenders?.map((t, i) => (
                            <tr key={i}>
                              <td>{t.method}</td>
                              <td className="text-end font-monospace">{t.amount?.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={6}>
                      <h6>Financial Reconciliation</h6>
                      <Table responsive bordered size="sm">
                        <tbody>
                          <tr>
                            <td>Gross Sales</td>
                            <td className="text-end font-monospace">{tiers.tier4_financial?.details?.gross_sales?.toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td>Discounts</td>
                            <td className="text-end font-monospace text-danger">-{tiers.tier4_financial?.details?.total_discount?.toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td>Taxes Collected</td>
                            <td className="text-end font-monospace">{tiers.tier4_financial?.details?.total_tax?.toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td>Unsettled Balance</td>
                            <td className={`text-end font-monospace ${tiers.tier4_financial?.details?.pending_balance > 0 ? 'text-danger fw-bold' : ''}`}>
                              {tiers.tier4_financial?.details?.pending_balance?.toFixed(2)}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                </div>
              )}
            </div>

            {/* Checkpoint Tier 5: Inventory & Stock Movement Guard */}
            <div className={`checkpoint-card status-${(tiers.tier5_inventory?.status || 'passed').toLowerCase()}`}>
              <div className="card-header" onClick={() => toggleTier('tier5')}>
                <div className="header-title">
                  <FontAwesomeIcon icon={faBoxes} />
                  <span>Tier 5: Inventory & Stock Movement Guard</span>
                </div>
                <div className="header-actions">
                  {getStatusBadge(tiers.tier5_inventory?.status)}
                  <FontAwesomeIcon icon={expandedTiers.tier5 ? faChevronUp : faChevronDown} className="text-muted" />
                </div>
              </div>
              {expandedTiers.tier5 && (
                <div className="card-body">
                  <p className="message-text">{tiers.tier5_inventory?.message}</p>
                  {tiers.tier5_inventory?.details?.pending_wastages_count > 0 && (
                    <Alert variant="warning" className="py-2 small">
                      <strong>Pending Wastages:</strong> {tiers.tier5_inventory.details.pending_wastages.map(w => w.wastage_number).join(', ')}
                    </Alert>
                  )}
                  {tiers.tier5_inventory?.details?.open_discrepancies_count > 0 && (
                    <Alert variant="danger" className="py-2 small">
                      <strong>Open Transfer Discrepancies:</strong> {tiers.tier5_inventory.details.open_discrepancies_count} records pending resolution.
                    </Alert>
                  )}
                </div>
              )}
            </div>

            {/* Manager Override Section (if soft warnings exist and no hard blocks) */}
            {evaluation.requires_manager_override && (
              <div className="manager-override-box">
                <div className="override-title">
                  <FontAwesomeIcon icon={faLock} />
                  Manager Override Required for Active Warnings
                </div>
                <p className="small text-muted mb-2">
                  One or more soft warnings (such as cash drawer variance or pending PMS checkout) were detected. A manager must sign off to proceed.
                </p>
                <Form.Group className="mb-2">
                  <Form.Check
                    type="checkbox"
                    id="manager-override-check"
                    label="I acknowledge and approve the detected warnings to close the business day"
                    checked={managerOverride}
                    onChange={(e) => setManagerOverride(e.target.checked)}
                  />
                </Form.Group>
                {managerOverride && (
                  <Form.Group>
                    <Form.Label className="small fw-bold">Override Justification / Audit Note *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Enter explanation for the discrepancy..."
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                    />
                  </Form.Group>
                )}
              </div>
            )}

            {/* Hard Block Banner - name the actual blockers */}
            {!evaluation.can_close && (
              <div className="blocked-banner">
                <FontAwesomeIcon icon={faTimesCircle} size="lg" />
                <div>
                  <strong>Day Close Blocked:</strong> Resolve before closing:
                  <ul className="mb-0 mt-1 ps-3">
                    {[tiers.tier1_orders, tiers.tier2_registers, tiers.tier3_folio, tiers.tier4_financial, tiers.tier5_inventory]
                      .filter(t => t?.status === 'BLOCKED')
                      .map(t => (
                        <li key={t.tier}><strong>{t.name}:</strong> {t.message}</li>
                      ))}
                  </ul>
                </div>
              </div>
            )}

            {validationError && (
              <Alert variant="danger" className="mt-3 mb-0">
                {validationError}
              </Alert>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={onRefresh} disabled={loading || closing}>
          <FontAwesomeIcon icon={faSync} spin={loading} className="me-1" /> Re-Check
        </Button>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onHide} disabled={closing}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmitClose}
            disabled={loading || closing || !evaluation.can_close || (evaluation.requires_manager_override && (!managerOverride || !overrideReason.trim()))}
          >
            {closing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Closing Day & Generating Audit...
              </>
            ) : (
              'Confirm & Close Day'
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default NightAuditCheckpointsModal;
